import { Container, Singleton } from 'typescript-ioc/es5'
import querystring from 'query-string'
import { AppService } from './app'
import { axiosFetch, isAxiosCancellation } from '../config/axios-setup'
import { AppState } from '../stores/app'
import aqe from '@pushly/aqe'
import {
    CreateDomainIntegrationRequest,
    GetDomainIntegrationResponse,
    GetShopifyDomainIntegrationResponse,
    GetSlackDomainIntegrationResponse,
    GetWordpressDomainIntegrationResponse,
    UpdateDomainIntegrationRequest,
} from '@pushly/models/lib/structs/domains'
import { IApiCallOptions, IApiResponse } from '@pushly/aqe/lib/interfaces'
import { DomainIntegrationProviders } from '@pushly/models/lib/enums'

@Singleton
export class DomainIntegrationService {
    public appService: AppService
    public appState: AppState

    public constructor() {
        this.appService = Container.get(AppService)
        this.appState = Container.get(AppState)
    }

    public async fetchAll(
        domainId: number,
        options: IApiCallOptions = {},
    ): Promise<IApiResponse<GetDomainIntegrationResponse[]>> {
        const res: IApiResponse<GetDomainIntegrationResponse[]> = {
            ok: false,
            data: [],
            meta: {},
            error: undefined,
            cancelled: false,
        }

        if (options.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            const req = await axiosFetch(
                'get',
                {
                    url: `${aqe.defaults.publicApiDomain}/v3/domains/${domainId}/integrations`,
                    config: {
                        skipCaseConversion: true,
                    },
                },
                options.cancellationKey,
            )

            const { data: reqData, ...meta } = req.data

            res.data = reqData.map((di) => GetDomainIntegrationResponse.parseJsonObject(di)) ?? []
            res.meta = meta
            res.ok = true
        } catch (err) {
            res.cancelled = isAxiosCancellation(err)
            res.error = err
        }

        if (options.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return res
    }

    public async fetchById(
        domainId: number,
        id: number,
        options: IApiCallOptions = {},
    ): Promise<IApiResponse<GetDomainIntegrationResponse>> {
        const res: IApiResponse<GetDomainIntegrationResponse> = {
            ok: false,
            data: undefined,
            meta: {},
            error: undefined,
            cancelled: false,
        }

        try {
            const req = await axiosFetch(
                'get',
                {
                    url: `${aqe.defaults.publicApiDomain}/domains/${domainId}/integrations/${id}`,
                    config: {
                        skipCaseConversion: true,
                    },
                },
                options.cancellationKey,
            )
            const { data: reqData, ...reqMeta } = req.data

            res.ok = true
            res.data = reqData
            res.meta = reqMeta
        } catch (err) {
            res.cancelled = isAxiosCancellation(err)
            res.error = err
        }

        return res
    }

    public async createInDomain(
        domainId: number,
        createDto: CreateDomainIntegrationRequest,
        options: IApiCallOptions = {},
    ): Promise<IApiResponse<GetDomainIntegrationResponse>> {
        const res: IApiResponse<GetDomainIntegrationResponse> = {
            ok: false,
            data: undefined,
            meta: {},
            error: undefined,
            cancelled: false,
        }

        if (options.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            const req = await axiosFetch(
                'post',
                {
                    url: `${aqe.defaults.publicApiDomain}/domains/${domainId}/integrations`,
                    data: createDto.serialize(),
                    config: {
                        skipCaseConversion: true,
                    },
                },
                options.cancellationKey,
            )
            const { data: reqData, ...reqMeta } = req.data

            res.ok = true
            res.data = GetDomainIntegrationResponse.parseJsonObject(reqData)
            res.meta = reqMeta
        } catch (err) {
            res.cancelled = isAxiosCancellation(err)
            res.error = err
        }

        if (options.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return res
    }

    public async updateById(
        domainId: number,
        update: UpdateDomainIntegrationRequest,
        options: IApiCallOptions = {},
    ): Promise<IApiResponse<GetDomainIntegrationResponse>> {
        const res: IApiResponse<GetDomainIntegrationResponse> = {
            ok: false,
            data: undefined,
            meta: {},
            error: undefined,
            cancelled: false,
        }

        if (options.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            const req = await axiosFetch(
                'patch',
                {
                    url: `${aqe.defaults.publicApiDomain}/domains/${domainId}/integrations/${update.id}`,
                    data: update.serialize(),
                    config: {
                        skipCaseConversion: true,
                    },
                },
                options.cancellationKey,
            )
            const { data: reqData, ...reqMeta } = req.data

            res.ok = true
            res.data = GetDomainIntegrationResponse.from(reqData)
            res.meta = reqMeta
        } catch (err) {
            res.cancelled = isAxiosCancellation(err)
            res.error = err
        }

        if (options.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return res
    }

    public async fetchIntegrationRedirectUrl(
        provider: string,
        domainId: number,
        data: any = {},
        cancellationKey?: string,
    ): Promise<IApiResponse<{ authUrl: string } | undefined>> {
        const res = {
            ok: false,
            data: undefined,
            meta: {},
            error: undefined,
            cancelled: false,
        }

        try {
            const req = await axiosFetch(
                'get',
                {
                    url: `${
                        aqe.defaults.publicApiDomain
                    }/domains/${domainId}/integrations/providers/${provider}?${querystring.stringify(data ?? {})}`,
                    data,
                },
                cancellationKey,
            )
            const { data: reqData, ...reqMeta } = req.data

            res.ok = true
            res.data = reqData
            res.meta = reqMeta
        } catch (err) {
            res.cancelled = isAxiosCancellation(err)
            res.error = err
        }

        return res
    }

    public async completeIntegrationSetup(
        domainId: number,
        provider: DomainIntegrationProviders.SHOPIFY,
        oauthParams: any,
        options?: IApiCallOptions,
    ): Promise<IApiResponse<GetShopifyDomainIntegrationResponse>>
    public async completeIntegrationSetup(
        domainId: number,
        provider: DomainIntegrationProviders.SLACK,
        oauthParams: any,
        options?: IApiCallOptions,
    ): Promise<IApiResponse<GetSlackDomainIntegrationResponse>>
    public async completeIntegrationSetup(
        domainId: number,
        provider: DomainIntegrationProviders.WORDPRESS,
        oauthParams: any,
        options?: IApiCallOptions,
    ): Promise<IApiResponse<GetWordpressDomainIntegrationResponse>>
    public async completeIntegrationSetup<T extends GetDomainIntegrationResponse>(
        domainId: number,
        provider: DomainIntegrationProviders,
        oauthParams: any,
        options: IApiCallOptions = {},
    ): Promise<IApiResponse<T>> {
        const res: IApiResponse<T> = {
            ok: false,
            data: undefined as any,
            meta: {},
            error: undefined,
            cancelled: false,
        }

        try {
            const req = await axiosFetch(
                'get',
                {
                    url: `${aqe.defaults.publicApiDomain}/domains/${domainId}/integrations/providers/${provider}/oauth/connect`,
                    config: {
                        params: oauthParams,
                        skipCaseConversion: true,
                    },
                },
                options.cancellationKey,
            )
            const { data: reqData, ...reqMeta } = req.data

            switch (provider) {
                case DomainIntegrationProviders.SHOPIFY: {
                    res.data = GetShopifyDomainIntegrationResponse.parseJsonObject(reqData) as T
                    break
                }
                case DomainIntegrationProviders.SLACK: {
                    res.data = GetSlackDomainIntegrationResponse.parseJsonObject(reqData) as T
                    break
                }
                case DomainIntegrationProviders.WORDPRESS: {
                    res.data = GetWordpressDomainIntegrationResponse.parseJsonObject(reqData) as T
                    break
                }
                default: {
                    res.data = reqData
                }
            }

            res.meta = reqMeta
            res.ok = true
        } catch (err) {
            res.cancelled = isAxiosCancellation(err)
            res.error = err
        }

        return res
    }

    public async removeIntegration(domainId: number, id: number, cancellationKey?: string): Promise<IApiResponse> {
        const res = {
            ok: false,
            data: undefined,
            meta: {},
            error: undefined,
            cancelled: false,
        }

        try {
            const req = await axiosFetch(
                'delete',
                {
                    url: `${aqe.defaults.publicApiDomain}/domains/${domainId}/integrations/${id}`,
                    config: {
                        skipCaseConversion: true,
                    },
                },
                cancellationKey,
            )
            const { data: reqData, ...reqMeta } = req.data

            res.ok = true
            res.data = reqData
            res.meta = reqMeta
        } catch (err) {
            res.cancelled = isAxiosCancellation(err)
            res.error = err
        }

        return res
    }
}
